import { jsPDF } from "jspdf"
import { svg2pdf } from "svg2pdf.js"
import { createRoot, type Root } from "react-dom/client"
import { flushSync } from "react-dom"
import { CarriageLabel } from "./templates/CarriageLabel"
import type { CarriageLabelRow } from "./carriageLabelsTypes"

// A5-landscape carriage-label PDF builder. Same off-screen render -> svg2pdf loop
// as pdfBuilder.buildMenuCardsPdf, but for CarriageLabel rows. Reuses
// downloadPdf/previewPdf from pdfBuilder unchanged.

export interface BuildLabelsArgs {
  rows: CarriageLabelRow[]
  onProgress?: (current: number, total: number) => void
  // Double-sided printing: emit each label twice, back-to-back (1,1,2,2,3,3…)
  // so a duplex printer lands both copies of a label on the same sheet.
  doubleSided?: boolean
}
export interface BuildLabelsResult {
  blob: Blob
  pageCount: number
  failed: { row: CarriageLabelRow; error: Error }[]
}

const W = 210
const H = 148

export async function buildCarriageLabelsPdf(args: BuildLabelsArgs): Promise<BuildLabelsResult> {
  if (args.rows.length === 0) throw new Error("No labels to render.")

  // For double-sided, duplicate each label consecutively (1,1,2,2,…) so the
  // pair prints on the two faces of one sheet. One page per entry either way.
  const rows = args.doubleSided ? args.rows.flatMap((r) => [r, r]) : args.rows

  const container = document.createElement("div")
  container.style.position = "fixed"
  container.style.left = "-99999px"
  container.style.top = "0"
  container.style.width = `${W}mm`
  container.style.height = `${H}mm`
  container.setAttribute("aria-hidden", "true")
  document.body.appendChild(container)

  const doc = new jsPDF({ unit: "mm", format: "a5", orientation: "landscape", putOnlyUsedFonts: true, compress: true })
  let root: Root | null = null
  const failed: BuildLabelsResult["failed"] = []

  try {
    root = createRoot(container)
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!
      if (i > 0) doc.addPage("a5", "landscape")
      try {
        flushSync(() => { root!.render(<CarriageLabel row={row} />) })
        const svgEl = container.querySelector("svg") as SVGSVGElement | null
        if (!svgEl) throw new Error("Label did not produce an <svg> element")
        await svg2pdf(svgEl, doc, { x: 0, y: 0, width: W, height: H })
      } catch (err) {
        failed.push({ row, error: err instanceof Error ? err : new Error(String(err)) })
      }
      args.onProgress?.(i + 1, rows.length)
    }
    return { blob: doc.output("blob"), pageCount: rows.length - failed.length, failed }
  } finally {
    if (root) { try { root.unmount() } catch { /* ignore */ } }
    if (container.parentNode) container.parentNode.removeChild(container)
  }
}
